/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import type { TeamStanding } from '@sportninja/common/features/team-balance/utils/teamStanding'
import { paragraphXSmall, weightSemiBold } from 'src/components/css'
import { InfoTooltip } from 'src/components/ts-components/RichTooltip'
import type { HeaderMoney } from '../utils/balanceTax'
import { balanceLabel, balanceText } from '../utils/balanceI18n'
import { LEGEND_ORDER, legendMeanings } from '../utils/standingCopy'
import type { StandingFormatters } from '../utils/standingCopy'
import { PRESENTATION } from './TeamPaymentStatusBadge'

type TeamPaymentStatusLegendProps = {
  standing: TeamStanding
  money: HeaderMoney
  fmt: StandingFormatters
}

/**
 * SN-8310 (Jeff, Sep 22: "I can just add an info icon") — what every team
 * payment status means, behind one icon beside the pill.
 *
 * The pill only ever shows the CURRENT status, so "what else can this say?"
 * had no answer on the page. Each row uses this team's own minimum, total and
 * dates, in the order a team moves through them, with the pill's own colours
 * (one `PRESENTATION` map). It ends with the rule a new league most needs
 * spelled out: the team owes the whole fee, filled spots or not.
 *
 * Same shape as the STATUS column's legend (`StatusColumnHeader`).
 */
const TeamPaymentStatusLegend = ({
  standing,
  money,
  fmt,
}: TeamPaymentStatusLegendProps) => {
  const meanings = legendMeanings(standing, money, fmt)
  const responsible = balanceText('common:teamPaymentStatusLegendResponsible', {
    total: fmt.money(money.amountDue),
  })
  const spoken = [
    balanceText('common:teamPaymentStatusLegendIntro'),
    ...LEGEND_ORDER.map(
      (code) =>
        `${balanceLabel(PRESENTATION[code].labelKey)}: ${meanings[code]}`
    ),
    responsible,
  ].join(' ')

  return (
    <InfoTooltip
      title={balanceLabel('common:teamPaymentStatusLegendTitle')}
      body={
        <div css={bodyStyle}>
          <ul css={listStyle} data-testid='team-payment-status-legend'>
            {LEGEND_ORDER.map((code) => (
              <li key={code} css={rowStyle}>
                <span
                  css={dotStyle}
                  style={{ background: PRESENTATION[code].color }}
                  aria-hidden='true'
                />
                <span css={labelStyle}>
                  {balanceLabel(PRESENTATION[code].labelKey)}
                </span>
                <span css={meaningStyle}>{meanings[code]}</span>
              </li>
            ))}
          </ul>
          <p css={footStyle}>{responsible}</p>
        </div>
      }
      bodyText={spoken}
      placement='bottomRight'
      maxWidth={360}
      size={16}
    />
  )
}

export default TeamPaymentStatusLegend

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
/** Dot, label, meaning — rows spread with `display: contents` so they line up. */
const listStyle = css`
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
`
const rowStyle = css`
  display: contents;
`
const dotStyle = css`
  width: 8px;
  height: 8px;
  margin-top: 4px;
  border-radius: 50%;
  align-self: start;
`
const labelStyle = css`
  ${paragraphXSmall};
  ${weightSemiBold};
  align-self: start;
  color: ${colors.NEUTRAL_0};
  white-space: nowrap;
`
const meaningStyle = css`
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_30};
`
const footStyle = css`
  ${paragraphXSmall};
  margin: 0;
  padding-top: 8px;
  border-top: 1px solid ${colors.SECONDARY_100};
  color: ${colors.NEUTRAL_50};
`
